import { FilterTypes } from "../../../handlers/filter";
import { isDescending } from "../../../handlers/sort";
/**
 * Mirrors `ListAdvertisersRequest.page_size`, which documents `0` as
 * defaulting to 100 server-side.
 */
export const DEFAULT_PAGE_SIZE = 100;
/**
 * The most rows `ListAdvertisersRequest.page_size` accepts — larger values
 * are rejected with InvalidArgument by protovalidate before the handler runs.
 */
export const MAX_PAGE_SIZE = 500;
/**
 * Builds the `order_by` value. The endpoint takes a bare field for ascending
 * and a `" desc"` suffix for descending — anything else is rejected with
 * InvalidArgument.
 */
export const buildSort = (sort) => isDescending(sort) ? `${sort.field} desc` : sort.field;
/**
 * Translates the SDK's `Filter<AdvertiserProfileFilters>` shape into the
 * query string `ListAdvertisers` expects.
 *
 * Only equality filters are forwarded. A string value emits one param; an
 * array value (`status`, `sub_status`, `source`, `industry`, `location`,
 * `products`) emits one repeated param per entry, which is how the endpoint
 * reads its repeated query params. `tab`, `query` and `include_archived` are
 * scalar and emit a single param each. Non-EQ filter types and other value
 * shapes are dropped rather than raising — callers pass generic filter shapes
 * down from higher up.
 *
 * Paging is emitted only when `filters.paging` is set. Omitting `page_size`
 * does not return the full set — the server falls back to 100 — so callers
 * that need everything have to walk `next_page_token`.
 */
export const toQueryParams = (filters, sort) => {
    // A list of pairs rather than an object so array-valued filters can emit the
    // same key more than once (repeated query params).
    const params = [];
    for (const where of filters?.where ?? []) {
        if (where.type !== FilterTypes.EQ)
            continue;
        // `id` is an SDK-side convention, not a query param this endpoint has. A
        // single-id find never reaches here, but a multi-id one does, and emitting
        // `id=a&id=b` would be dropped server-side and answered with the
        // unfiltered list.
        if (where.field === "id")
            continue;
        if (typeof where.value === "string") {
            params.push([where.field, where.value]);
        }
        else if (typeof where.value === "boolean") {
            // `include_archived` is the only boolean the endpoint takes, and the
            // gateway reads it from the string form.
            params.push([where.field, String(where.value)]);
        }
        else if (Array.isArray(where.value)) {
            for (const entry of where.value) {
                if (typeof entry === "string")
                    params.push([where.field, entry]);
            }
        }
    }
    if (filters?.paging) {
        params.push(["page_size", String(filters.paging.size || DEFAULT_PAGE_SIZE)]);
        if (filters.paging.token) {
            // The token encodes an offset server-side, so it is replayed verbatim.
            params.push(["page_token", filters.paging.token]);
        }
    }
    if (sort?.field && sort.direction) {
        params.push(["order_by", buildSort(sort)]);
    }
    const encoded = params
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
    return encoded ? `?${encoded}` : "";
};
//# sourceMappingURL=utils.js.map